import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "../../store";
import { setActiveTV, setListTV } from "../../store/activeTV/activeTVSlice";
import { useLazyGetAllContentQuery, useLazyGetPageContentQuery } from "../../api/modules";
import helper from "../../constants/helper";
import { ProgramPages } from "../../types";
import { IRelated } from "../../components/Cards/types";

interface UseProgramChannelsProps {
    id: number | string | null | undefined;
    isLiveType: boolean;
    isProgramContextPage?: boolean;
    contentContext: any;
    isVisible: boolean;
    canOpenProgramLayout: boolean;
    eventNotStartedOverlayVisible: boolean;
    handleLayout: () => void;
}

export const useProgramChannels = ({
    id,
    isLiveType,
    isProgramContextPage,
    contentContext,
    isVisible,
    canOpenProgramLayout,
    eventNotStartedOverlayVisible,
    handleLayout
}: UseProgramChannelsProps) => {
    const dispatch = useDispatch();
    const { listTV, activeTV } = useSelector((state: RootState) => state.tvContent);
    const profileID = localStorage.getItem(helper.EPIC_PROFILE_ID) as string;
    const role = localStorage.getItem(helper.EPIC_USER_ROLE);

    const [fetchAllProgramContent] = useLazyGetAllContentQuery();
    const [fetchProgramCategoryContent] = useLazyGetPageContentQuery();

    const [isChannelListFetching, setIsChannelListFetching] = useState(false);
    const [isChannelListLoadingMore, setIsChannelListLoadingMore] = useState(false);
    const [channelListPage, setChannelListPage] = useState(1);
    const [hasMoreChannels, setHasMoreChannels] = useState(false);

    const channelListScrollRef = useRef<HTMLDivElement | null>(null);
    const channelListSentinelRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        setChannelListPage(1);
        setHasMoreChannels(false);
    }, [id, contentContext?.page, contentContext?.categoryId, contentContext?.region]);

    const fetchProgramChannels = useCallback(
        async ({ page = 1, append = false }: { page?: number; append?: boolean } = {}) => {
            if (!isLiveType || !isProgramContextPage || !contentContext) return;
            const parsedProfileId = profileID ? Number(profileID) : null;
            const sharedParams = {
                page: contentContext.page, role,
                ...(parsedProfileId ? { profile_id: parsedProfileId } : {}),
                page_number: page,
                rehydrated_pagination: true,
                ...(contentContext.page === ProgramPages.LIVE && contentContext.region
                    ? { region: contentContext.region } : {}),
            };
            append ? setIsChannelListLoadingMore(true) : setIsChannelListFetching(true);
            try {
                const response = contentContext.categoryId === 0
                    ? await fetchAllProgramContent(sharedParams).unwrap()
                    : await fetchProgramCategoryContent({ ...sharedParams, content_id: contentContext.categoryId }).unwrap();
                const results: IRelated[] = response?.results ?? [];
                const mergedResults: IRelated[] = append ? [...listTV, ...results] : results;
                dispatch(setListTV(mergedResults));
                if (!append) {
                    const currentActive =
                        mergedResults.find((item: IRelated) => item.id === activeTV?.id) ||
                        mergedResults.find((item: IRelated) => item.id === id);
                    if (currentActive) dispatch(setActiveTV(currentActive));
                }
                setChannelListPage(page);
                setHasMoreChannels(Boolean(response?.next));
            } catch (error) {
                console.error("Failed to fetch program channel list", error);
            } finally {
                append ? setIsChannelListLoadingMore(false) : setIsChannelListFetching(false);
            }
        },
        [isLiveType, isProgramContextPage, contentContext, profileID, role,
            fetchAllProgramContent, fetchProgramCategoryContent, listTV, activeTV?.id, dispatch, id]
    );

    const handleLayoutClick = useCallback(() => {
        if (!isVisible && canOpenProgramLayout) void fetchProgramChannels({ page: 1, append: false });
        handleLayout();
    }, [isVisible, canOpenProgramLayout, fetchProgramChannels, handleLayout]);

    const handleLoadMoreChannels = useCallback(() => {
        if (!canOpenProgramLayout || isChannelListFetching || isChannelListLoadingMore || !hasMoreChannels) return;
        void fetchProgramChannels({ page: channelListPage + 1, append: true });
    }, [canOpenProgramLayout, isChannelListFetching, isChannelListLoadingMore,
        hasMoreChannels, channelListPage, fetchProgramChannels]);

    useEffect(() => {
        if (!isVisible || !canOpenProgramLayout || !id) return;
        if (eventNotStartedOverlayVisible || activeTV?.id) return;
        const currentPlaying = listTV.find((item: any) => item.id === id);
        if (currentPlaying) dispatch(setActiveTV(currentPlaying));
    }, [isVisible, canOpenProgramLayout, id, listTV, activeTV?.id, eventNotStartedOverlayVisible, dispatch]);

    useEffect(() => {
        if (!isVisible || !canOpenProgramLayout) return;
        const root = channelListScrollRef.current;
        const sentinel = channelListSentinelRef.current;
        if (!root || !sentinel) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry?.isIntersecting) handleLoadMoreChannels(); },
            { root, rootMargin: "250px 0px", threshold: 0 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [isVisible, canOpenProgramLayout, handleLoadMoreChannels]);

    return {
        channelListScrollRef,
        channelListSentinelRef,
        isChannelListFetching,
        isChannelListLoadingMore,
        handleLayoutClick,
        listTV
    };
};
